import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { X, ChevronLeft, ChevronRight, ArrowUpRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

// items: [{url, name, category, slug, alt}]
export default function Lightbox({ items, index, onClose, onIndex }) {
  const open = index != null && index >= 0;
  const touchX = useRef(null);

  const prev = useCallback(() => onIndex((index - 1 + items.length) % items.length), [index, items, onIndex]);
  const next = useCallback(() => onIndex((index + 1) % items.length), [index, items, onIndex]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [open, prev, next, onClose]);

  if (!open) return null;
  const item = items[index];

  return (
    <AnimatePresence>
      <motion.div
        data-testid="lightbox"
        className="fixed inset-0 z-[100] bg-ink/95 flex flex-col"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onTouchStart={(e) => (touchX.current = e.touches[0].clientX)}
        onTouchEnd={(e) => {
          if (touchX.current == null) return;
          const dx = e.changedTouches[0].clientX - touchX.current;
          if (dx > 50) prev(); else if (dx < -50) next();
          touchX.current = null;
        }}
      >
        <div className="flex items-center justify-between px-5 py-4 text-white">
          <span className="text-sm text-white/70" data-testid="lightbox-counter">{index + 1} / {items.length}</span>
          <button onClick={onClose} data-testid="lightbox-close" aria-label="Close" className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center">
            <X size={22} />
          </button>
        </div>

        <div className="flex-1 relative flex items-center justify-center px-4 select-none" onClick={onClose}>
          <button onClick={(e) => { e.stopPropagation(); prev(); }} data-testid="lightbox-prev" aria-label="Previous" className="absolute left-3 md:left-6 z-10 w-12 h-12 rounded-full bg-white/10 hover:bg-gold text-white flex items-center justify-center transition-colors">
            <ChevronLeft size={26} />
          </button>
          <motion.img
            key={item.url}
            src={item.url}
            alt={item.alt || item.name}
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }}
            className="max-h-[74vh] max-w-[92vw] object-contain rounded-lg"
          />
          <button onClick={(e) => { e.stopPropagation(); next(); }} data-testid="lightbox-next" aria-label="Next" className="absolute right-3 md:right-6 z-10 w-12 h-12 rounded-full bg-white/10 hover:bg-gold text-white flex items-center justify-center transition-colors">
            <ChevronRight size={26} />
          </button>
        </div>

        <div className="px-6 py-5 text-center text-white">
          {item.category && <div className="text-xs uppercase tracking-wider text-gold-light mb-1">{item.category}</div>}
          <h3 className="text-lg font-semibold">{item.name}</h3>
          {item.slug && (
            <Link to={`/projects/${item.slug}`} onClick={onClose} data-testid="lightbox-view-project" className="inline-flex items-center gap-1.5 mt-3 text-sm font-semibold text-gold hover:text-gold-light">
              View Full Project <ArrowUpRight size={15} />
            </Link>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
